import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  Card, CardContent
} from "@mui/material";
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from "sweetalert2";
import apiClient from "@/api/client";

const statusOptions = ["Approved", "Pending", "Rejected"];

const RequestPage = ({ title, fetchUrl, editEndpoint, deleteEndpoint, statusEndpoint }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [statusToUpdate, setStatusToUpdate] = useState(null);
  const [reason, setReason] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [showError, setShowError] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(fetchUrl);
      const json = response.data;
      setData(json?.data?.data || json?.data || []);
    } catch (err) {
      Swal.fire("Error", "Could not fetch data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (row) => {
    setEditData(row);
    setOpenDialog(true);
  };

  const handleEditSubmit = async () => {
    try {
      await apiClient.put(`${editEndpoint}/${editData.id}`, editData);
      Swal.fire("Success", "Data updated", "success");
      setOpenDialog(false);
      fetchData();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    }
  };

  const handleDelete = async (item) => {
    Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: `You are about to delete ${item.first_name || item.name || "this entry"}.`,
      showCancelButton: true,
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await apiClient.delete(`/delete-${deleteEndpoint}/${item.id}`);
          Swal.fire("Deleted!", "The record has been removed.", "success");
          fetchData();
        } catch (err) {
          Swal.fire("Error", "Failed to delete", "error");
        }
      }
    });
  };

  const handleStatusChange = (item, newStatus) => {
    if (item.status !== newStatus) {
      setStatusToUpdate({ ...item, newStatus });
      setReason("");
      setShowError(false); // reset error when opening dialog
      setModalOpen(true);
    }
  };

  const handleConfirmStatusChange = async () => {
    // Validate reason
    if (!reason.trim()) {
      setShowError(true);
      return;
    }
    setShowError(false);

    try {
      const payload = {
        status: statusToUpdate.newStatus,
        status_reason: reason,
      };

      await apiClient.put(`${statusEndpoint}/${statusToUpdate.id}/status`, payload);

      Swal.fire("Success", "Status updated", "success");
      setModalOpen(false);
      setReason("");
      setStatusToUpdate(null);
      fetchData();
    } catch (err) {
      console.error("Error updating status:", err);
      Swal.fire("Error", err.response?.data?.message || err.message || "Failed to update status", "error");
    }
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="div">
              {title}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {loading && <CircularProgress />}
      {!loading && data?.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: "text.secondary" }}>
              All Records
            </Typography>
            <hr />
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {Object.keys(data[0]).map((key) => (
                      <TableCell key={key}>
                        <Typography
                          variant="subtitle2"
                          sx={{ textTransform: "capitalize" }}
                        >
                          {key.replace(/_/g, " ")}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <IconButton
                          onClick={() => handleEdit(item)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(item)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                      {Object.entries(item).map(([key, value]) => (
                        <TableCell key={key}>
                          {key === "status" ? (
                            <Select
                              value={value}
                              size="small"
                              onChange={(e) =>
                                handleStatusChange(item, e.target.value)
                              }
                            >
                              {statusOptions.map((s) => (
                                <MenuItem key={s} value={s}>
                                  {s}
                                </MenuItem>
                              ))}
                            </Select>
                          ) : typeof value === "string" && value.length > 50 ? (
                            <Typography variant="body2">
                              {value.substring(0, 50)}...
                            </Typography>
                          ) : (
                            String(value)
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
      {!loading && data?.length === 0 && (
        <Typography align="center" sx={{ mt: 2 }}>
          No data found.
        </Typography>
      )}

      {/* Status Dialog */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>Reason for Status Change</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (showError && e.target.value.trim()) setShowError(false);
            }}
            required
            error={showError && reason.trim() === ''}
            helperText={showError && reason.trim() === '' ? 'Reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmStatusChange}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Entry</DialogTitle>
        <DialogContent>
          {editData && Object.entries(editData).map(([key, value]) => (
            <TextField
              key={key}
              label={key.replace(/_/g, " ")}
              value={value}
              fullWidth
              margin="dense"
              onChange={(e) =>
                setEditData((prev) => ({ ...prev, [key]: e.target.value }))
              }
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequestPage;